<template>
  <!-- The hike map page's warnings (SAC-SECTIONS.md "Addition"): the same
       round alerts button as the planner, left of the location control, only
       while the hike's route has warnings; the "Warnings on route" sheet it
       opens; a hazard row opens the read-only hazard details, a difficult
       section's row shows the section on the map (sheet closed, map fitted,
       section lit, popup). The sections are drawn over the route either way,
       and a click on one's line opens its popup. A map click (closing the
       popup) or reopening the sheet lets the section go. The same warnings
       follow a map's selected-hike card (SelectedHikeRouteAlerts), with that
       page's screen and panels. Opened from a warning on the hike's page
       (?alert=), the map shows that one as its row would: a hazard centred
       with its details and pager, a section framed with its popup. -->
  <RouteAlertsButton :count="alerts.count" :expanded="sheetOpen" @open="openSheet" />
  <Teleport to="body">
    <RouteAlertsSheet
      :open="sheetOpen && alerts.count > 0 && !detailsOpen"
      :rows="rows"
      :panel-style="panelStyle"
      @close="closeSheet"
      @select="onSelect"
    />
  </Teleport>
  <HazardDetailsModal
    ref="details"
    :screen="detailsScreen || screen"
    place-to-map
    :pager="hazardPagerView"
    @gone="reload"
    @close="onDetailsClosed"
    @pager-previous="stepHazard('previous')"
    @pager-next="stepHazard('next')"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import RouteAlertsButton from './components/RouteAlertsButton.vue';
import RouteAlertsSheet from './components/RouteAlertsSheet.vue';
import HazardDetailsModal from '../trail_hazards/HazardDetailsModal.vue';
import emitter from '../../utils/bus';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { HikeRouteAlertsLoader } from '../../utils/routeAlerts/routeAlertsLoader';
import { EMPTY_ROUTE_ALERTS, buildRouteAlertCardRows } from '../../utils/routeAlerts/routeAlertsModel';
import type { RouteAlert, RouteAlertRowView, RouteAlerts } from '../../utils/routeAlerts/routeAlertsModel';
import { sectionCallout } from '../../utils/routeAlerts/sacSections';
import { RouteSectionHighlighter, focusPadding } from '../../utils/routeAlerts/sectionHighlights';
import { createSectionPopup } from '../../utils/routeAlerts/sectionPopup';
import { centreAbovePanel, centreMapOn } from '../../utils/routeAlerts/mapFocus';
import { hazardPanelStyle } from '../../utils/trailHazards/hazardPanelPlacement';
import {
  routeAlertPagedEvent,
  routeAlertTappedEvent,
  routeAlertsOpenedEvent,
  routeAlertsShownEvent,
  trackRouteAlertsEvent,
} from '../../utils/routeAlerts/routeAlertsAnalytics';
import { HazardSelectionRing } from '../../utils/trailHazards/hazardSelectionRing';
import { setSectionFocused } from '../../utils/routeAlerts/selectedHikeCard';
import { withoutAlertParam } from '../../utils/routeAlerts/warningsLink';
import { mapboxMapOf } from '../../utils/routeAlerts/sectionHighlights';
import { currentAlert, pagerFor, pagerView, refreshPager, stepPager } from '../../utils/routeAlerts/routeAlertGroups';
import type { PagerDirection, PagerView, RouteAlertPager } from '../../utils/routeAlerts/routeAlertGroups';
import type { SectionCalloutPager } from '../../utils/routeAlerts/sectionHighlights';
import type { RouteAlertsScreen } from '../../utils/routeAlerts/routeAlertsAnalytics';

/** The hike map page's elevation profile, along the bottom of the map. */
const ELEVATION_PROFILE = '.elevation-profile-container:not(.collapsed)';

export default defineComponent({
  name: 'HikeMapRouteAlerts',
  components: { RouteAlertsButton, RouteAlertsSheet, HazardDetailsModal },
  props: {
    hikeId: { type: [String, Number], required: true },
    /** The `screen` its route-alert events carry. */
    screen: { type: String as PropType<RouteAlertsScreen>, default: 'hike_map' },
    /** Send route_alerts_shown (not where a card's chip already does). */
    reportShown: { type: Boolean, default: true },
    /** The `screen` the hazard details' hazard_viewed carries; the route-alert screen by default. */
    detailsScreen: { type: String, default: null },
    /** The panel along the bottom of the map a framed section must clear. */
    bottomPanelSelector: { type: String, default: ELEVATION_PROFILE },
    /** A panel down the map's right side a framed section must clear. */
    dockSelector: { type: String, default: null },
    /** A warning to show once the map is ready (the hike page's ?alert=). */
    initialAlertId: { type: String, default: null },
  },
  data() {
    return {
      alerts: EMPTY_ROUTE_ALERTS as RouteAlerts,
      sheetOpen: false,
      panelStyle: null as Record<string, string> | null,
      shownTracked: false,
      /** The hazard details' pager through the card's group (‹ 1 / 3 ›); null for none. */
      hazardPager: null as RouteAlertPager | null,
      /**
       * A hazard's details are open over the list: the list steps aside (a long
       * one would show below the panel) and comes back when they close.
       */
      detailsOpen: false,
      /** The warning still to show once both it and the map are ready. */
      pendingFocusId: null as string | null,
      /** The map has loaded (its first route fit may still be running). */
      mapReady: false,
    };
  },
  computed: {
    metric(): boolean {
      try {
        return MeasurementPrefUtility.preference().isMetric();
      } catch {
        return true; // km by default.
      }
    },
    rows(): RouteAlertRowView[] {
      const t = (key: string, values?: Record<string, unknown> | number) => this.$t(key, values ?? {}) as string;
      return buildRouteAlertCardRows(this.alerts, { metric: this.metric, locale: this.$i18n?.locale }, t);
    },
    hazardPagerView(): PagerView | null {
      return pagerView(this.hazardPager, this.translate);
    },
  },
  created() {
    this.pendingFocusId = this.initialAlertId || null;
    (this as any).loader = new HikeRouteAlertsLoader(graph_client as any, reportHandledError, this.screen);
    (this as any).sections = new RouteSectionHighlighter({
      getMap: () => (window as any).map,
      getRegistry: () => (window as any).layerRegistry,
      createPopup: createSectionPopup,
      calloutFor: (alert: RouteAlert) => this.calloutFor(alert),
      pagerFor: (alert: RouteAlert) => this.sectionPagerFor(alert),
      clickable: true,
      // While a section is shown, the page's own map clicks leave its card alone.
      onSelectionChange: (sectionId: string | null) => setSectionFocused(this.$.uid, sectionId !== null),
      // Clear of the panel along the bottom of the map (and one down its side).
      focusPadding: () => {
        const map = document.querySelector('.mapboxgl-map');
        const bottom = this.bottomPanelSelector ? document.querySelector(this.bottomPanelSelector) : null;
        const dock = this.dockSelector ? document.querySelector(this.dockSelector) : null;
        return focusPadding({
          mapRect: map ? map.getBoundingClientRect() : null,
          leftPanelRight: null,
          dockLeft: dock ? dock.getBoundingClientRect().left : null,
          bottomPanelTop: bottom ? bottom.getBoundingClientRect().top : null,
        });
      },
    });
    // The ring under the hazard the details show, while they're open.
    (this as any).ring = new HazardSelectionRing({
      getMap: () => (window as any).map,
      getRegistry: () => (window as any).layerRegistry,
    });
  },
  mounted() {
    // The map is made after this mounts, and a style switch drops its layers.
    emitter.on('map-loaded', this.redraw);
    emitter.on('map-loaded', this.onMapLoaded);
    emitter.on('map-style-loaded', this.redraw);
    window.addEventListener('resize', this.position);
    const map = mapboxMapOf((window as any).map) as any;
    this.mapReady = !!map?.loaded?.();
    this.load();
  },
  beforeUnmount() {
    emitter.off('map-loaded', this.redraw);
    emitter.off('map-loaded', this.onMapLoaded);
    emitter.off('map-style-loaded', this.redraw);
    window.removeEventListener('resize', this.position);
    (this as any).loader.cancel();
    (this as any).sections.dispose();
    (this as any).ring.dispose();
    setSectionFocused(this.$.uid, false);
  },
  methods: {
    async load() {
      const result = await (this as any).loader.load(this.hikeId);
      if (result.kind === 'stale') return;
      this.alerts = result.kind === 'loaded' ? result.alerts : EMPTY_ROUTE_ALERTS;
      if (this.alerts.count === 0) this.sheetOpen = false;
      (this as any).sections.setAlerts(this.alerts.alerts);
      // The open hazard's group, as it now stands (no pager once it is alone).
      this.hazardPager = refreshPager(this.hazardPager, this.alerts.alerts);
      if (this.reportShown && this.alerts.count > 0 && !this.shownTracked) {
        this.shownTracked = true;
        trackRouteAlertsEvent(routeAlertsShownEvent(this.alerts, this.screen));
      }
      this.showPendingFocus();
    },

    /**
     * The map's load: its first fit to the route runs right after this event,
     * so the warning waits for that to settle (showPendingFocus).
     */
    onMapLoaded() {
      this.mapReady = true;
      setTimeout(() => this.showPendingFocus(), 0);
    },

    /**
     * The warning the page was opened on, shown as its row would show it, once
     * the warnings have loaded and the map is ready and still. Shown once, and
     * taken off the address; one that's no longer on the route shows nothing.
     */
    showPendingFocus() {
      const id = this.pendingFocusId;
      if (!id || !this.mapReady || this.alerts === EMPTY_ROUTE_ALERTS) return;
      const map = mapboxMapOf((window as any).map) as any;
      if (!map) return;
      if (map.isMoving?.()) {
        map.once?.('idle', () => this.showPendingFocus());
        return;
      }
      this.pendingFocusId = null;
      try {
        window.history.replaceState(window.history.state, '', withoutAlertParam(window.location.href));
      } catch {
        // The address keeps it; the map still shows the warning.
      }
      const alert = this.alerts.alerts.find((candidate) => candidate.id === id);
      if (!alert) return;
      const sections = (this as any).sections as RouteSectionHighlighter;
      if (alert.kind === 'difficult_terrain') {
        if (!sections.focus(alert.id)) centreMapOn((window as any).map, alert);
        return;
      }
      if (alert.hazard?.id) this.showHazard(alert, alert.hazard.id);
    },

    /** A hazard of the list: centred (above its details on a phone), ringed, its details with its group's pager. */
    showHazard(alert: RouteAlert, hazardId: string) {
      centreAbovePanel((window as any).map, alert);
      this.hazardPager = pagerFor(this.alerts.alerts, alert);
      this.detailsOpen = true;
      ((this as any).ring as HazardSelectionRing).show(alert);
      (this.$refs.details as any)?.open(hazardId);
    },

    /** A hazard left the map since the list loaded: fetch the list again. */
    reload() {
      this.load();
    },

    redraw() {
      (this as any).sections.sync();
    },

    openSheet() {
      // From a hazard's details: back to the list.
      if (this.detailsOpen) {
        (this.$refs.details as any)?.close();
        this.sheetOpen = true;
        this.position();
        return;
      }
      if (this.sheetOpen) {
        this.closeSheet();
        return;
      }
      // The list again: whatever was shown on the map is let go.
      (this as any).sections.clearSelection();
      this.sheetOpen = true;
      this.position();
      trackRouteAlertsEvent(routeAlertsOpenedEvent(this.alerts.count, this.screen));
    },

    closeSheet() {
      this.sheetOpen = false;
    },

    onSelect(row: RouteAlertRowView) {
      trackRouteAlertsEvent(routeAlertTappedEvent(row.alert, this.screen, row.moreCount));
      const sections = (this as any).sections as RouteSectionHighlighter;
      if (row.sectionId) {
        this.closeSheet();
        if (!sections.focus(row.sectionId)) centreMapOn((window as any).map, row);
        return;
      }
      sections.clearSelection();
      if (!row.hazardId) {
        centreMapOn((window as any).map, row);
        return;
      }
      // Its details open over the map; the hazard stays in view (above them on a phone).
      this.showHazard(row.alert, row.hazardId);
    },

    /** An arrow of the hazard details' pager: the map, the ring and the details move on. */
    stepHazard(direction: PagerDirection) {
      const next = stepPager(this.hazardPager, direction);
      const alert = currentAlert(next);
      if (!next || !alert?.hazard) return;
      this.hazardPager = next;
      centreAbovePanel((window as any).map, alert);
      ((this as any).ring as HazardSelectionRing).show(alert);
      (this.$refs.details as any)?.open(alert.hazard.id, { keepDetails: true });
      trackRouteAlertsEvent(routeAlertPagedEvent(alert, this.screen, next.index + 1, next.members.length, direction));
    },

    /** The details closed (or the hazard left the map): no pager, no ring. */
    onDetailsClosed() {
      this.hazardPager = null;
      this.detailsOpen = false;
      ((this as any).ring as HazardSelectionRing).clear();
    },

    /** A section's popup arrows, when its grade has more sections on the route. */
    sectionPagerFor(alert: RouteAlert): SectionCalloutPager | null {
      const pager = pagerFor(this.alerts.alerts, alert);
      const view = pagerView(pager, this.translate);
      if (!pager || !view) return null;
      return {
        ...view,
        onPrevious: () => this.stepSection(pager, 'previous'),
        onNext: () => this.stepSection(pager, 'next'),
      };
    },

    stepSection(pager: RouteAlertPager, direction: PagerDirection) {
      const next = stepPager(pager, direction);
      const alert = currentAlert(next);
      if (!next || !alert) return;
      ((this as any).sections as RouteSectionHighlighter).focus(alert.id, { focusArrow: direction });
      trackRouteAlertsEvent(routeAlertPagedEvent(alert, this.screen, next.index + 1, next.members.length, direction));
    },

    translate(key: string, values?: Record<string, unknown> | number): string {
      return this.$t(key, values ?? {}) as string;
    },

    calloutFor(alert: RouteAlert) {
      const t = (key: string, values?: Record<string, unknown> | number) => this.$t(key, values ?? {}) as string;
      return sectionCallout(alert.sacGrade ?? 3, alert, { metric: this.metric, locale: this.$i18n?.locale }, t);
    },

    position() {
      const map = document.querySelector('.mapboxgl-map');
      const dock = this.dockSelector ? document.querySelector(this.dockSelector) : null;
      this.panelStyle = hazardPanelStyle({
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        mapRect: map ? map.getBoundingClientRect() : null,
        dockLeft: dock ? dock.getBoundingClientRect().left : null,
      });
    },
  },
});
</script>
